/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import { useInfluencer } from "@/Providers/Influencer";
import { useGeneralSettings } from "@/Providers/General";
import { useStaticData } from "@/Providers/Data";
import { useCookies } from "react-cookie";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Check,
  HeartIcon,
  MessageCircle,
  PlusIcon,
  Repeat2,
} from "lucide-react";
import { toast } from "@/shadcnComponents/ui/use-toast";
import { cn } from "@/lib/utils";
import {
  formatFollowersCount,
  formatNumberWithCommas,
} from "@/Helpers/format-helper";
import {
  ScrollArea,
  ScrollBar,
} from "../../../shadcnComponents/ui/scroll-area";
import { FiShare } from "react-icons/fi";
import { FaArrowRight } from "react-icons/fa6";
import featureLogo from "./assets/featured.gif";
import { twMerge } from "tailwind-merge";
import { IoPeopleOutline } from "react-icons/io5";
import { MdAutoGraph } from "react-icons/md";

// Lazy Loaded Components
const InfluencerTopPosts = lazy(
  () => import("./PublicProfileComponent/InfluencerTopPosts")
);
const InfluDetailsChart = lazy(
  () => import("./PublicProfileComponent/InfluDetailsChart")
);
const CollaborationPortfolioCard = lazy(
  () => import("./PublicProfileComponent/CollaborationPortfolioCard")
);
const MonetizationAvenueCard = lazy(
  () => import("./PublicProfileComponent/MonetizationAvenueCard")
);
const SectionCard = lazy(() => import("./PublicProfileComponent/SectionCard"));
const TagChip = lazy(() => import("./PublicProfileComponent/TagChip"));
const UnlockForCollaborationCard = lazy(
  () => import("./PublicProfileComponent/UnlockForCollaborationCard")
);
const ProfileButtonSelector = lazy(
  () => import("./PublicProfileComponent/ProfileButtonSelector")
);
const PopupCard = lazy(() => import("./PublicProfileComponent/PopupCard"));
const ShinyButton = lazy(() => import("@/shadcnComponents/ui/shinny-button"));
const LoginPopup = lazy(
  () =>
    import("../CreatorLandingPage/CreatorLandingPageComponent.tsx/LoginPopup")
);
const TopLinksCard = lazy(
  () => import("./PublicProfileComponent/TopLinksCard")
);
const VerticalDividerVector = lazy(
  () => import("./PublicProfileComponent/VerticalDividerVector")
);
const HorizontalDividerVector = lazy(
  () => import("./PublicProfileComponent/HorizontalDividerVector")
);
const AudienceAvenuesCard = lazy(
  () => import("./PublicProfileComponent/AudienceAvenuesCard")
);
const NotFoundPage = lazy(() => import("./ProfileErrorPage"));
const HighlightMetricsCard = lazy(
  () => import("./PublicProfileComponent/HighlightMetricsCard")
);

const LazyWrapper = ({ children, skeleton: SkeletonComponent, count = 1 }) => (
  <Suspense
    fallback={
      SkeletonComponent ? (
        count > 1 ? (
          <div className="flex gap-4 flex-wrap">
            {Array.from({ length: count }, (_, i) => (
              <SkeletonComponent key={i} />
            ))}
          </div>
        ) : (
          <SkeletonComponent />
        )
      ) : (
        <div className="animate-pulse h-20 bg-gray-700 rounded"></div>
      )
    }
  >
    {children}
  </Suspense>
);

// const host = import.meta.env.VITE_BACKEND_SERVER_URL;

type UnlockPopupProps = {
  slug: string;
  onClose: () => void;
  title?: string;
  icon?: string;
  body?: string;
  buttonText?: string;
};

const UnlockPopup: React.FC<UnlockPopupProps> = ({
  slug,
  onClose,
  title,
  icon,
  body,
  buttonText,
}) => {
  const [, setCookie] = useCookies();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    mixpanel.track(` ${buttonText} Popup Button clicked`);
    setCookie("unlockCreatorSearch", slug, { maxAge: 86400 });
    navigate("/Brand/DashBoard/CollabWithInfluencer");
  };

  return (
    <PopupCard onClose={onClose}>
      <div className="flex flex-col text-center gap-4 text-[#FFF] font-Inter not-italic w-full">
        <div className="text-[32px] font-semibold leading-[normal]">{icon}</div>
        <div className="flex flex-col gap-2">
          <div className="text-[16px] font-semibold leading-[18px]">
            {title}
          </div>
          <div className="text-[#9093AA] text-[12px] font-normal leading-[18px]">
            {body}
          </div>
        </div>
        <div>
          <Button variant="destructive" onClick={() => handleSubmit()}>
            <span className="text-[14px] font-semibold leading-[24px]">
              {buttonText}
            </span>
          </Button>
        </div>
      </div>
    </PopupCard>
  );
};

type themeColorType = {
  color: string[];
  bg: string[];
  from: string[];
  iconbg: string[];
};

const themeColor: themeColorType = {
  color: ["#171717", "#0D0D0D", "#181414", "#100F0A", "#0E0E15"],
  bg: [
    "bg-[#171717]",
    "bg-[#0D0D0D]",
    "bg-[#181414]",
    "bg-[#100F0A]",
    "bg-[#0E0E15]",
  ],
  from: [
    "from-[#171717]",
    "from-[#0D0D0D]",
    "from-[#181414]",
    "from-[#100F0A]",
    "from-[#0E0E15]",
  ],
  iconbg: [
    "bg-[#3e1a1a]",
    "bg-[#3d371b]",
    "bg-[#403e40]",
    "bg-[#182330]",
    "bg-[#210b43]",
  ],
};

export const InfluencerPublicProfile: React.FC = () => {
  // Hooks ----------
  const { slug } = useParams();
  const influencerState = useInfluencer();
  const generalState = useGeneralSettings();
  const staticDataState = useStaticData();
  const [cookies, setCookies] = useCookies(["referal_code"]);
  const targetRef = useRef(null);
  const targetRef2 = useRef(null);

  // States --------------
  const [InfluData, setInfluData] = useState<any>({});
  const [topInfluencer, setTopInfluencer] = useState([]);
  const [openUnlockPopup, setOpenUnlockPopup] = useState(false);
  const [openCollabPopup, setOpenCollabPopup] = useState(false);
  const [isVisibleFloater, setIsVisibleFloater] = useState(false); // for floater visibility
  const [themeColorState, setThemeColorState] = useState({
    bgColorClass: "",
    gradientColorFromClass: "",
    IconBgClass: "",
  });
  const [openLoginPopup, setOpenLoginPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [TempInfluData, setTempInfluData] = useState<{
    avgLikes: number;
    avgComments: number;
    avgEngagement: number;
    avgEngagementPercent: any;
    avgReposts: number;
    followers: number;
    topThreeEngagedPosts: [];
    name: string;
    gender: string;
    tagline: string;
    location: string;
    profile_image: string;
    experience: string;
    badge: string;
    featured: [];
    profile_url: string;
    firstThirtyLikes: number[];
    firstThirtyComments: number[];
    topLinks: any[];
  }>();
  const [influContentCategory, SetInfluContentCategory] = useState<string[]>();
  const [influAudienceCategory, SetInfluAudienceCategory] = useState(null);
  const [showErrorPage, setShowErrorPage] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState("");

  //master data
  const [masterContentCategory, setMasterContentCategory] = useState([]);
  const [masterContentFormat, setMasterContentFormat] = useState([]);
  const [masterAudienceTypes, setMasterAudienceTypes] = useState([]);
  const [masterAudiencePlatforms, setMasterAudiencePlatforms] = useState([]);
  const [masterCollabGoals, setMasterCollabGoals] = useState([]);
  const [masterMonetizationPlatform, setMasterMonetizationPlatform] = useState(
    []
  );
  const [masterMonetizationServices, setMasterMonetizationServices] = useState(
    []
  );

  const checkLoginStatus = useCallback(() => {
    return (
      localStorage.getItem("jwtToken") &&
      localStorage.getItem("UserType") === "Influencer"
    );
  }, []);

  useEffect(() => {
    staticDataState?.getNewDataFromType("Content Type").then((e) => {
      setMasterContentCategory(e);
    });
    staticDataState?.getNewDataFromType("Content Format").then((e) => {
      setMasterContentFormat(e);
    });
    staticDataState
      ?.getNewDataFromType("lkd_super_master_audienceType")
      .then((e) => {
        setMasterAudienceTypes(e);
      });
    staticDataState?.getNewDataFromType("Audience Platform").then((e) => {
      setMasterAudiencePlatforms(e);
    });
    staticDataState?.getNewDataFromType("Goal / KPI").then((e) => {
      setMasterCollabGoals(e);
    });
    staticDataState?.getNewDataFromType("Platform Name").then((e) => {
      setMasterMonetizationPlatform(e);
    });
    staticDataState?.getNewDataFromType("Services list").then((e) => {
      setMasterMonetizationServices(e);
    });
  }, []);

  // Effects  ---------------------------

  // Dynamic Theme Color
  useEffect(() => {
    const randomColor = (listOfColor: themeColorType) => {
      const index = Math.floor(Math.random() * listOfColor?.bg?.length);
      return [
        listOfColor?.bg[index],
        listOfColor?.from[index],
        listOfColor?.iconbg[index],
      ];
    };

    if (themeColor) {
      const indx = randomColor(themeColor);

      setThemeColorState({
        bgColorClass: indx[0],
        gradientColorFromClass: indx[1],
        IconBgClass: indx[2],
      });
    }
  }, []);

  // Get Influencer Data
  useEffect(() => {
    mixpanel.track("Visited Profile Page Collab");
    generalState.setLoading(true);
    const getInfluencerData = async () => {
      await influencerState.getInfluencerInfoFromSlug(slug).then((resData) => {
        if (resData.success) {
          const getTempInfluencerData = async () => {
            await influencerState
              .getTempInfluencerLinkedinProfileFromSlug(slug)
              .then((resData) => {
                if (resData.success) {
                  if (!resData?.LinkedinAnalysis?.name) {
                    // generalState.setLoading(false);
                    return; //navigate("/influencer/dashboard");
                  }
                  setTempInfluData(resData?.LinkedinAnalysis);
                  SetInfluContentCategory(resData?.ContentCategory);
                  SetInfluAudienceCategory(resData?.audienceCategory);
                  // generalState.setLoading(false);
                } else {
                  alert(resData?.error || "Some Error occurred!");
                  //navigate("/");
                }
              });
          };
          getTempInfluencerData();

          setInfluData((prev) => ({
            ...prev,
            profileData: resData?.data,
            collabHistory: resData?.data2?.data,
            monetizationChannels: resData?.data3?.data,
            contentCategories: resData?.data4?.categories,
            contentFormat: resData?.data4?.formats,
            audiencePlatform: resData?.data5?.platforms,
            audienceTypes: resData?.data5?.types,
            topLinks: resData?.data5?.topLinks,
          }));

          if (resData?.data?.referal_code) {
            setCookies("referal_code", resData?.data?.referal_code, {
              maxAge: 86400,
            });
          }
          generalState.setLoading(false);
        } else {
          generalState.setLoading(false);
          // alert(resData?.error || "Some Error occurred!");
          setShowErrorPage(true);
          // navigate("/");
        }
      });
    };

    getInfluencerData();
  }, []);

  // Get Top Influencer
  useEffect(() => {
    const getFeaturedCreators = async () => {
      const result = await influencerState?.getFeaturedCreators();
      if (result?.success) {
        setTopInfluencer(result?.topInfluencer);
      } else {
        console.log("Some Error occurred!");
      }
    };
    getFeaturedCreators();
  }, []);

  // Filter the data2 and data3
  useEffect(() => {
    if (InfluData && InfluData?.collabHistory) {
      const newData = InfluData?.collabHistory.filter(
        (item) =>
          item?.companyName &&
          item?.companyName !== "None" &&
          item?.companyName !== "NA" &&
          item?.companyName !== "null"
      );
      // Update the state with the filtered data2
      setInfluData((prevData) => ({
        ...prevData,
        collabHistory: newData,
      }));
    }
    if (InfluData && InfluData?.monetizationChannels) {
      const newData = InfluData?.monetizationChannels.filter(
        (item) =>
          item?.platformName &&
          masterMonetizationPlatform?.find(
            (platform) => platform?._id === item?.platformName
          )?.monetizationPlatformName !== "None"
      );
      // Update the state with the filtered data2
      setInfluData((prevData) => ({
        ...prevData,
        monetizationChannels: newData,
      }));
    }

    // console.log(InfluData);
  }, [InfluData?.profileData]);

  // Floating Button
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      // Adjust this value based on the client height where you want to show the component
      const triggerHeight = targetRef?.current?.clientHeight;
      const triggerHeight2 = targetRef2?.current?.clientHeight;

      // console.log("scroll: ", scrollY, "target-Creator Profile:", triggerHeight, "target2-Creator Data Section:", triggerHeight2);

      // Check if the scroll position has crossed the trigger height
      setIsVisibleFloater(scrollY > triggerHeight);

      if (scrollY > triggerHeight2) {
        setIsVisibleFloater(false);
      }
    };

    // Attach the event listener when the component mounts
    window.addEventListener("scroll", handleScroll);

    // Detach the event listener when the component unmounts
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []); // Run the effect only once when the component mounts

  // handler function ---------------------------

  // Share Profile
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `anchors|collab`,
          text: `${InfluData?.profileData?.name} is open for collaboration`,
          url: window.location.href,
        });
      } catch (error) {
        console.error("Something went wrong while sharing", error);
      }
    } else {
      console.log("Web Share API is not supported in your browser.");
    }
  };

  const handleKudos = async (collabId: string, index?: number) => {
    if (!checkLoginStatus()) {
      mixpanel.track("Kudos Button Clicked without login");
      setOpenLoginPopup(true);
      return;
    }

    try {
      setLoading(true);
      // console.log(`Influencer Kudos Given for collabId: ${collabId} at index: ${index}, ${InfluData?.kudosData?.find((kudos) => kudos?.collabId === collabId)?.isKudosGiven}`); // -- debug

      const isKudosGiven = InfluData?.collabHistory?.[index]?.isKudosGiven;
      if (isKudosGiven) {
        await influencerState
          ?.removeKudosFromCollab(collabId)
          .then((resData) => {
            if (resData?.success) {
              mixpanel.track("Kudos Removed, source profile", {
                collabId: collabId,
                position: index + 1,
              });
              setInfluData((prevData) => {
                const updatedCollabHistory = [
                  ...(prevData?.collabHistory ?? []),
                ];

                updatedCollabHistory[index] = {
                  ...updatedCollabHistory[index],
                  isKudosGiven: false,
                  kudosCount: updatedCollabHistory[index]?.kudosCount - 1,
                };
                return {
                  ...prevData,
                  collabHistory: updatedCollabHistory,
                };
              });
            }
          });
        setLoading(false);
      } else {
        await influencerState
          ?.giveKudosToCollab(collabId, "profile")
          .then((resData) => {
            if (resData?.success) {
              mixpanel.track("Kudos Given, source profile", {
                collabId: collabId,
                position: index + 1,
              });
              setInfluData((prevData) => {
                const updatedCollabHistory = [
                  ...(prevData?.collabHistory ?? []),
                ];
                updatedCollabHistory[index] = {
                  ...updatedCollabHistory[index],
                  isKudosGiven: true,
                  kudosCount: updatedCollabHistory[index]?.kudosCount + 1,
                };
                return {
                  ...prevData,
                  collabHistory: updatedCollabHistory,
                };
              });
            }

            setLoading(false);
          });
      }
    } catch (error) {
      console.log(
        "Error giving kudos:",
        error?.response ? error?.response?.data : error?.message
      );
    }
  };

  const handleFollow = async () => {
    if (!checkLoginStatus()) {
      mixpanel.track("Follow Button Clicked without login");
      setOpenLoginPopup(true);
      return;
    }

    if (InfluData?.profileData?.ownProfile) return;

    try {
      setLoading(true);
      if (InfluData?.profileData?.isFollowing) {
        await influencerState
          ?.unfollowInfluencer(InfluData?.profileData?._id)
          .then((resData) => {
            if (resData?.success) {
              mixpanel.track("Unfollowed Influencer, Source Profile", {
                influencerId: InfluData?.profileData?._id,
              });
              setInfluData((prevData) => ({
                ...prevData,
                profileData: {
                  ...prevData?.profileData,
                  isFollowing: false,
                },
              }));
              toast({
                title: `You've unfollowed ${InfluData?.profileData?.name}`,
              });
            }
            setLoading(false);
          });
      } else {
        await influencerState
          ?.followInfluencer(InfluData?.profileData?._id, "profile")
          .then((resData) => {
            if (resData?.success) {
              mixpanel.track("Followed Influencer, Source Profile", {
                influencerId: InfluData?.profileData?._id,
              });
              setInfluData((prevData) => ({
                ...prevData,
                profileData: {
                  ...prevData?.profileData,
                  isFollowing: true,
                },
              }));
              toast({
                title: `You're following ${InfluData?.profileData?.name}`,
              });
            }
            setLoading(false);
          });
      }
    } catch (error) {
      console.log(
        "Error following influencer:",
        error?.response ? error?.response?.data : error?.message
      );
    }
  };

  useEffect(() => {
    const selectedProfileImage =
      InfluData?.profileData?.profile ||
      TempInfluData?.profile_image ||
      "https://collab.anchors.in/FeatredCreatorBackground.png";

    // Preload the image
    const img = new Image();
    img.onload = () => {
      setImageSrc(selectedProfileImage);
      setImageLoaded(true);
    };
    img.onerror = () => {
      setImageSrc("https://collab.anchors.in/FeatredCreatorBackground.png");
      setImageLoaded(true);
    };
    img.src = selectedProfileImage;
  }, [InfluData?.profileData?.profile, TempInfluData?.profile_image]);

  return (
    <>
      {openUnlockPopup && (
        <UnlockPopup
          slug={slug}
          onClose={() => {
            mixpanel.track("Unlock popup closed");
            setOpenUnlockPopup(false);
          }}
          icon="🔒"
          title="Contact Info Locked!"
          body="This information is not publicly available.
                    Please proceed to unlock it if you'd like to connect with the creator directly."
          buttonText="🫱🏼‍🫲🏽 Unlock Now"
        />
      )}

      {openCollabPopup && (
        <UnlockPopup
          slug={slug}
          onClose={() => {
            mixpanel.track("Collab popup closed");
            setOpenCollabPopup(false);
          }}
          title="Ready to Collaborate?"
          body={`Partner with ${InfluData?.profileData?.name} and explore synergies. Connect directly and bring your vision to life.`}
          buttonText="🫱🏼‍🫲🏽 Connect Now!"
        />
      )}

      {openLoginPopup && (
        <LoginPopup onClose={() => setOpenLoginPopup(false)} backToSource />
      )}

      <div
        className={cn(
          "w-full min-h-svh bg-[#171717]",
          `${themeColorState.bgColorClass}`
        )}
      >
        {showErrorPage ? (
          <NotFoundPage />
        ) : (
          <div
            className={twMerge(
              `text-white min-h-svh bg-[#171717] md:px-[96px] md:py-[30px] relative md:max-w-[1580px] md:mx-auto`,
              `${themeColorState.bgColorClass}`
            )}
          >
            {/* floating button - create your profile for desktop */}
            <div className="hidden md:block fixed bottom-0 right-0 p-4 z-10">
              <div className="flex items-center gap-[12px]">
                <div className="items-center justify-center animate-border inline-block rounded-[8px] bg-white bg-gradient-to-r from-red-500 via-purple-500 to-blue-500 bg-[length:400%_400%] p-[2px]">
                  <ShinyButton
                    className="flex px-[12px] py-[8px] justify-center items-center gap-[4.75px] self-stretch rounded-[6px] border-[1px] border-solid  text-[#FFF] text-center font-[Inter] not-italic leading-[normal] bg-black"
                    onClick={() => {
                      mixpanel.track("Create your Collab Profile");
                      const url = `/influencer?refer=${
                        cookies?.referal_code || ""
                      }`;
                      window.open(url, "_blank");
                    }}
                  >
                    <span className="text-[16px] font-normal">🪄</span>
                    <span className="text-[12px] font-semibold">
                      Create your Collab Profile
                    </span>
                  </ShinyButton>
                </div>
              </div>
            </div>

            {/* main */}
            {/* <div className="md:flex md:gap-[28px] w-full h-max"> */}
            <div className="md:grid md:grid-cols-12 md:gap-[28px] w-full h-max">
              {/* Creator Profile */}
              <div className="col-span-4">
                {/* md:w-[365px]  */}
                <section
                  ref={targetRef}
                  className="shrink-0  md:flex md:flex-col md:gap-[28px]"
                >
                  <div className="md:p-[16px] md:rounded-[8px] md:border-[1px] md:border-solid md:border-[#262626]">
                    <div className="relative h-[359px]">
                      {(() => {
                        const selectedProfileImage =
                          InfluData?.profileData?.profile ||
                          TempInfluData?.profile_image ||
                          "https://collab.anchors.in/FeatredCreatorBackground.png";
                        return (
                          <img
                            src={selectedProfileImage}
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null; // prevents looping
                              currentTarget.src =
                                "https://collab.anchors.in/FeatredCreatorBackground.png";
                            }}
                            alt={`${InfluData?.profileData?.name} Pic`}
                            className="z-[1] object-cover h-full w-full md:rounded-[8px]"
                            loading="eager"
                            fetchPriority="high"
                            decoding="async"
                            onLoad={() => setImageLoaded(true)}
                            style={{
                              // Prevent layout shift
                              aspectRatio: "1/1",
                              // Smooth loading transition
                              opacity: imageLoaded ? 1 : 0,
                              transition: "opacity 0.3s ease-in-out",
                            }}
                          />
                        );
                      })()}

                      {/* Placeholder during loading */}
                      {!imageLoaded && (
                        <div className="absolute inset-0 z-[0] bg-gray-800 animate-pulse md:rounded-[8px] flex items-center justify-center">
                          <div className="w-16 h-16 border-4 border-gray-600 border-t-white rounded-full animate-spin"></div>
                        </div>
                      )}

                      <div
                        className={twMerge(
                          `z-[2] absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-t from-[#171717] to-transparent`,
                          `${themeColorState.gradientColorFromClass}`
                        )}
                      >
                        <div className="flex flex-col justify-between w-full h-full">
                          <div></div>
                          <h1 className="w-full text-center text-white text-[40px] not-italic font-bold leading-[normal] font-[Inter] px-[16px]">
                            {InfluData?.profileData?.name}
                          </h1>
                        </div>
                      </div>
                    </div>

                    <div className="px-[16px]">
                      {topInfluencer.some(
                        (item) => item?._id === InfluData?.profileData?._id
                      ) && (
                        <div className="mt-[16px] flex justify-center">
                          <div className="flex justify-center items-center gap-2 px-3 py-1.5 rounded bg-[linear-gradient(92deg,_rgba(241,_241,_241,_0.16)_1.57%,_rgba(243,_171,_171,_0.00)_49.29%,_rgba(241,_241,_241,_0.20)_96.06%)]">
                            <div className="w-6 h-6">
                              <img
                                src={featureLogo}
                                alt="logo"
                                className="w-full h-full"
                              />
                            </div>
                            <span className="rounded text-white text-center text-xs not-italic font-bold leading-[normal] font-[Inter]">
                              FEATURED
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="mt-[16px] mb-[32px] flex flex-col items-center gap-4">
                        {/* feature tag */}
                        <h2 className="text-[#D9D9D9] text-center text-sm not-italic font-normal leading-6 tracking-[0.14px] self-stretch">
                          {InfluData?.profileData?.tagline}
                        </h2>

                        {/* Social Links */}
                        <div
                          className="w-full flex justify-center  items-center overflow-x-auto"
                          style={{ scrollbarWidth: "none" }}
                        >
                          <div className="w-fit flex justify-center gap-3 flex-wrap">
                            <ProfileButtonSelector
                              profileData={InfluData?.profileData}
                              color={themeColorState.IconBgClass}
                            />
                          </div>
                        </div>

                        {/* Follow button */}
                        {!InfluData?.profileData?.ownProfile &&
                          InfluData?.profileData && (
                            <div className="flex flex-col items-center gap-4">
                              <Button
                                className={`group/follow-btn text-[#ffff] font-bold font-[Inter] text-[12px] not-italic leading-[normal] w-full gap-2 bg-transparent border border-[#F1F1F1] hover:bg-[#F1F1F1] hover:text-[#171717] hover:border-[#F1F1F1] py-[8px] px-[16px] rounded-full ${
                                  InfluData?.isFollowing
                                    ? "bg-[#F1F1F1] text-[#171717] border-[#F1F1F1]"
                                    : ""
                                }`}
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  handleFollow();
                                }}
                              >
                                {InfluData?.profileData?.isFollowing ? (
                                  <>
                                    <Check size={16} /> Following
                                  </>
                                ) : (
                                  <>
                                    <PlusIcon size={16} /> Follow
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                      </div>

                      {/* CTA Button --- Under Profile */}
                      <div
                        className={twMerge(
                          `flex items-start gap-[16px]`,
                          `${themeColorState.bgColorClass}`
                        )}
                      >
                        <button
                          className="flex px-[16px] py-[8px] justify-center items-center gap-[10px] self-stretch rounded-[6px] bg-[#E2E8F0]"
                          onClick={() => {
                            mixpanel.track("share profile");
                            handleShare();
                          }}
                          aria-label="share profile"
                        >
                          <FiShare size={20} color="#000000" />
                        </button>

                        {/* collaborate with me button */}
                        <button
                          className="flex px-[16px] py-[8px] justify-center items-center gap-[10px] flex-[1_0_0] rounded-[6px] bg-[#DC2627]"
                          onClick={() => {
                            mixpanel.track("Collaborate with me");
                            setOpenCollabPopup(true);
                          }}
                        >
                          <h2 className="text-[#FFF] text-[14px] not-italic font-medium leading-[24px] font-[Inter]">
                            Collaborate with me
                          </h2>
                          <FaArrowRight size={16} color="#FFF" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Unlock for collaboration! - desktop */}
                  <section className="hidden md:block">
                    <UnlockForCollaborationCard
                      onUnlockClick={() => {
                        mixpanel.track("Unlock button click");
                        setOpenUnlockPopup(true);
                      }}
                      onCollabClick={() => setOpenCollabPopup(true)}
                    />
                  </section>
                </section>
              </div>
              {/* Vertical Divider */}
              <div className="md:hidden">
                <VerticalDividerVector />
              </div>

              {/* Creator Data Section*/}
              <div className="col-span-8">
                <section ref={targetRef2} className="px-[16px] md:px-0 w-full">
                  {/* Top links ---------------------- */}
                  {(InfluData?.topLinks?.length > 0 ||
                    TempInfluData?.topLinks?.[0]?.link?.length > 0) && (
                    <>
                      <div>
                        <SectionCard
                          icon={"📌"}
                          title="My Top Links"
                          subtitle="Links to my top social media profiles & websites"
                        >
                          <div className="flex items-start flex-wrap gap-4">
                            {(InfluData?.topLinks?.length > 0
                              ? InfluData?.topLinks
                              : TempInfluData?.topLinks || []
                            )?.map((item, index) => {
                              if (item?.link_text)
                                item.linkText = item?.link_text;
                              return (
                                <TopLinksCard
                                  index={index}
                                  icon={item?.icon}
                                  link={item?.link}
                                  linkText={item?.linkText}
                                />
                              );
                            })}
                          </div>
                        </SectionCard>
                      </div>
                      <VerticalDividerVector />
                    </>
                  )}

                  {/* Profile Highlights ---------------------- */}
                  {Object?.entries(TempInfluData || {})?.length > 0 && (
                    <>
                      <div className="">
                        <SectionCard
                          icon={"🫱🏼‍🫲🏽"}
                          title="Profile Highlights"
                          subtitle="A quick glance at some key stats"
                        >
                          <div
                            className="w-full overflow-x-auto"
                            style={{ scrollbarWidth: "none" }}
                          >
                            <ul className="w-full grid grid-cols-2 md:flex items-start gap-[15px] whitespace-nowrap">
                              {[
                                {
                                  icon: (
                                    <IoPeopleOutline
                                      color="#568CDD"
                                      size={25}
                                    />
                                  ),
                                  title: "Total Followers",
                                  value:
                                    TempInfluData?.followers?.toString()
                                      ?.length > 7
                                      ? formatFollowersCount(
                                          TempInfluData?.followers
                                        )
                                      : formatNumberWithCommas({
                                          num: TempInfluData?.followers || 0,
                                        }) || 0,
                                },
                                {
                                  icon: <HeartIcon color="#DC2627" size={25} />,
                                  title: "Avg Likes",
                                  value: TempInfluData?.avgLikes
                                    ? Math.ceil(TempInfluData?.avgLikes)
                                    : 0,
                                },
                                {
                                  icon: (
                                    <MessageCircle color="#1070E0" size={25} />
                                  ),
                                  title: "Avg Comments",
                                  value: TempInfluData?.avgComments
                                    ? Math.ceil(TempInfluData?.avgComments)
                                    : 0,
                                },
                                {
                                  icon: <Repeat2 color="#13931D" size={25} />,
                                  title: "Avg Reposts",
                                  value: TempInfluData?.avgReposts
                                    ? Math.ceil(TempInfluData?.avgReposts)
                                    : 0,
                                },
                                {
                                  icon: (
                                    <MdAutoGraph color="#F59E00" size={25} />
                                  ),
                                  title: "Avg Eng.",
                                  value: `${
                                    TempInfluData?.avgEngagementPercent
                                      ? `${
                                          Math.ceil(
                                            TempInfluData?.avgEngagementPercent *
                                              10
                                          ) / 10
                                        }%`
                                      : 0
                                  }`,
                                },
                              ].map((item, i) => {
                                if (
                                  !item?.value ||
                                  parseFloat(item?.value) <= 0
                                )
                                  return null;
                                return (
                                  <li
                                    key={i}
                                    className="in-w-[200px] flex-shrink-0 h-full"
                                  >
                                    <HighlightMetricsCard
                                      icon={item?.icon}
                                      value={item?.value}
                                      title={item?.title}
                                    />
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </SectionCard>
                      </div>
                    </>
                  )}

                  {/* Post Wise Engagement -----------------------*/}
                  {(TempInfluData?.firstThirtyLikes?.length > 0 ||
                    TempInfluData?.firstThirtyComments?.length) && (
                    <>
                      <VerticalDividerVector />
                      <InfluDetailsChart
                        firstThirtyComments={TempInfluData?.firstThirtyComments}
                        firstThirtyLikes={TempInfluData?.firstThirtyLikes}
                      />
                    </>
                  )}

                  {/* Top engaged posts.......... */}
                  {TempInfluData?.topThreeEngagedPosts?.length > 0 && (
                    <>
                      <VerticalDividerVector />
                      <InfluencerTopPosts
                        TopPosts={TempInfluData?.topThreeEngagedPosts}
                        influencer={{
                          name: TempInfluData?.name,
                          profile: TempInfluData?.profile_image,
                          tagline: TempInfluData?.tagline,
                        }}
                      />
                    </>
                  )}

                  {/* Collaboration Portfolio/Collaboration History ------------------------- */}
                  {InfluData?.collabHistory?.length > 0 && (
                    <>
                      <VerticalDividerVector />
                      <div className="">
                        <SectionCard
                          icon={"🫱🏼‍🫲🏽"}
                          title="Collaboration History"
                          subtitle="Brands I've partnered with in the past"
                        >
                          {/* <div className="flex items-start flex-wrap gap-[20px]"> */}
                          <div className="w-full overflow-hidden">
                            <ScrollArea>
                              <div className="grid md:flex gap-4 pb-2">
                                {InfluData?.collabHistory?.map(
                                  (data, index) => {
                                    return (
                                      data?.companyName?.trim().length > 0 && (
                                        <CollaborationPortfolioCard
                                          key={index}
                                          companyName={data?.companyName}
                                          companyProfile={data?.companyProfile}
                                          companyUrl={data?.companyUrl}
                                          goals={data?.goals?.map((goal) => {
                                            const GoalName =
                                              masterCollabGoals?.find(
                                                (masterGoal) =>
                                                  masterGoal?._id === goal
                                              );
                                            return GoalName?.CollabGoalsName;
                                          })}
                                          postLink={data?.postLink}
                                          status={
                                            data?.collabVerification?.status
                                          }
                                          onKudos={() =>
                                            handleKudos(data?._id, index)
                                          }
                                          isKudosGiven={data?.isKudosGiven}
                                          kudosCount={
                                            data?.kudosCount?.toString() || "0"
                                          }
                                          loading={loading}
                                        />
                                      )
                                    );
                                  }
                                )}
                                <ScrollBar orientation="horizontal" />
                              </div>
                            </ScrollArea>
                          </div>
                        </SectionCard>
                      </div>
                    </>
                  )}

                  {/* Monetization Avenue/Monetization Channels ------------------------- */}
                  {InfluData?.monetizationChannels?.length > 0 && (
                    <>
                      <VerticalDividerVector />

                      <div className="">
                        <SectionCard
                          icon={"💰"}
                          title="Monetization Channels"
                          subtitle="Platforms where I monetize my content & reach"
                        >
                          {/* <div className="flex items-start flex-wrap gap-[20px] w-full"> */}
                          <div className="w-full overflow-hidden">
                            <ScrollArea>
                              <div className="grid md:flex gap-4 pb-2">
                                {InfluData?.monetizationChannels?.map(
                                  (data, index) => {
                                    return (
                                      <MonetizationAvenueCard
                                        key={index}
                                        platformName={
                                          masterMonetizationPlatform?.find(
                                            (platform) =>
                                              platform?._id ===
                                              data?.platformName
                                          )?.monetizationPlatformName
                                        }
                                        profileLink={data?.profileLink}
                                        paidAudience={data?.paidAudience}
                                        methods={data?.methods?.map(
                                          (method) => {
                                            const methodName =
                                              masterMonetizationServices?.find(
                                                (masterMethod) =>
                                                  masterMethod?._id === method
                                              );
                                            return methodName?.monetizationServiceName;
                                          }
                                        )}
                                        platformLogo={
                                          masterMonetizationPlatform?.find(
                                            (platform) =>
                                              platform?._id ===
                                              data?.platformName
                                          )?.monetizationPlatformIcon
                                        }
                                      />
                                    );
                                  }
                                )}
                                <ScrollBar orientation="horizontal" />
                              </div>
                            </ScrollArea>
                          </div>
                        </SectionCard>
                      </div>
                    </>
                  )}

                  {/* Content Category/Content Focus --------------------------*/}
                  {(() => {
                    const selectedCategory =
                      influContentCategory?.length > 0
                        ? influContentCategory
                        : InfluData?.contentCategories || [];
                    if (selectedCategory?.length > 0) {
                      return (
                        <>
                          <VerticalDividerVector />

                          <div className="">
                            <SectionCard
                              icon={"💭"}
                              title="Content Focus"
                              subtitle="Niche categories & topics I majorly focus on"
                            >
                              <div className="flex items-start gap-[8px] flex-wrap">
                                {selectedCategory
                                  ?.slice(0, 6)
                                  .map((data, index) => {
                                    return (
                                      <TagChip
                                        key={index}
                                        title={
                                          masterContentCategory?.find(
                                            (category) => category?._id === data
                                          )?.categoryName || data
                                        }
                                      />
                                    );
                                  })}
                                {selectedCategory?.length > 6 && (
                                  <TagChip
                                    title={`+${selectedCategory?.length - 6}`}
                                  />
                                )}
                              </div>
                            </SectionCard>
                          </div>
                        </>
                      );
                    }
                  })()}

                  {/* Content Formats ---------------------*/}
                  {InfluData?.contentFormat?.length > 0 && (
                    <>
                      <VerticalDividerVector />

                      <div className="">
                        <SectionCard
                          icon={"💭"}
                          title="Content Formats"
                          subtitle="Format styles that I am most comfortable with"
                        >
                          <div className="flex items-start gap-[8px] flex-wrap">
                            {InfluData?.contentFormat
                              ?.slice(0, 6)
                              .map((data, index) => {
                                return (
                                  <TagChip
                                    key={index}
                                    title={
                                      masterContentFormat?.find(
                                        (category) => category?._id === data
                                      )?.contentFormatName
                                    }
                                  />
                                );
                              })}
                            {InfluData?.contentFormat?.length > 6 && (
                              <TagChip
                                title={`+${
                                  InfluData?.contentFormat?.length - 6
                                }`}
                              />
                            )}
                          </div>
                        </SectionCard>
                      </div>
                    </>
                  )}

                  {/* Audience Types --------------------*/}
                  {(() => {
                    const selectedAudience =
                      influAudienceCategory?.length > 0
                        ? influAudienceCategory
                        : InfluData?.audienceTypes;
                    if (selectedAudience?.length > 0) {
                      return (
                        <>
                          <VerticalDividerVector />

                          <div className="">
                            <SectionCard
                              icon={"👩🏻‍🤝‍👩🏼"}
                              title="Audience Types"
                              subtitle="Demographics of my audience & community"
                            >
                              <div className="flex items-start gap-[8px] flex-wrap">
                                {selectedAudience
                                  ?.slice(0, 6)
                                  .map((data, index) => {
                                    return (
                                      <TagChip
                                        key={index}
                                        title={
                                          data?.value ||
                                          masterAudienceTypes?.find(
                                            (audience) => audience?._id === data
                                          )?.LkdAudienceTypeName
                                        }
                                      />
                                    );
                                  })}
                                {selectedAudience?.length > 6 && (
                                  <TagChip
                                    title={`+${selectedAudience?.length - 6}`}
                                  />
                                )}
                              </div>
                            </SectionCard>
                          </div>
                        </>
                      );
                    }
                  })()}

                  {/* Audience Avenues/Platform Presence --------------------------*/}
                  {InfluData?.audiencePlatform?.filter(
                    (data) => data?.oldplatform !== "LinkedIn Profile"
                  )?.length > 0 && (
                    <>
                      <VerticalDividerVector />

                      <div className="">
                        <SectionCard
                          icon={"👩🏻‍🤝‍👩🏼"}
                          title="Platform Presence"
                          subtitle="Platforms I have a strong reach & community on"
                        >
                          <div className="flex items-start gap-[20px] self-stretch flex-wrap">
                            {InfluData?.audiencePlatform?.map((data, index) => {
                              return (
                                data?.audience > 0 &&
                                data?.platform && (
                                  <AudienceAvenuesCard
                                    key={index}
                                    platform={
                                      masterAudiencePlatforms?.find(
                                        (platform) =>
                                          platform?._id === data?.platform
                                      )?.audiencePlatformName
                                    }
                                    audience={data?.audience}
                                    link={data?.link}
                                    icon={
                                      masterAudiencePlatforms?.find(
                                        (platform) =>
                                          platform?._id === data?.platform
                                      )?.audiencePlatformIcon
                                    }
                                  />
                                )
                              );
                            })}
                          </div>
                        </SectionCard>
                      </div>
                    </>
                  )}
                </section>
              </div>
            </div>

            {/* CTA button --- floating Mobile */}
            {isVisibleFloater && (
              <div
                className={cn(
                  "z-[5] bottom-0 left-0 right-0 fixed p-4 space-y-4 md:hidden",
                  `${themeColorState.bgColorClass}`
                )}
              >
                <div className="flex items-start gap-[16px]">
                  {/* share button */}
                  <button
                    className="flex px-[16px] py-[8px] justify-center items-center gap-[10px] self-stretch rounded-[6px] bg-[#E2E8F0]"
                    onClick={() => {
                      mixpanel.track("share profile");
                      handleShare();
                    }}
                  >
                    <FiShare size={20} color="#000000" />
                  </button>

                  {/* collaborate with me button */}
                  <button
                    className="flex px-[16px] py-[8px] justify-center items-center gap-[10px] flex-[1_0_0] rounded-[6px] bg-[#DC2627]"
                    onClick={() => {
                      mixpanel.track("Collaborate with me");
                      setOpenCollabPopup(true);
                    }}
                  >
                    <span className="text-[#FFF] text-[14px] not-italic font-medium leading-[24px] font-[Inter]">
                      Collaborate with me
                    </span>
                    <FaArrowRight size={16} color="#FFF" />
                  </button>
                </div>

                {/* create your profile */}
                <div className="">
                  <div className="flex justify-center items-center gap-[12px]">
                    <span className="text-[#A3A3A3] text-center font-[Inter] text-[14px] not-italic font-normal leading-[18px]">
                      Influencer?
                    </span>
                    <button
                      className="flex px-[12px] py-[8px] justify-center items-center gap-[4.75px] self-stretch rounded-[6px] border-[1px] border-solid border-[#FFF] text-[#FFF] text-center font-[Inter] not-italic leading-[normal] bg-black"
                      onClick={() => {
                        mixpanel.track("Create your Collab Profile");
                        const url = `/influencer?refer=${
                          cookies?.referal_code || ""
                        }`;
                        window.open(url, "_blank");
                      }}
                    >
                      <span className="text-[16px] font-normal">🪄</span>
                      <span className="text-[12px] font-semibold">
                        Create your Collab Profile
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Unlock for collaboration! - mobile */}
            <section className="md:hidden">
              <div className="px-[16px] py-[32px]">
                <UnlockForCollaborationCard
                  onUnlockClick={() => {
                    mixpanel.track("Unlock button click");
                    setOpenUnlockPopup(true);
                  }}
                  onCollabClick={() => setOpenCollabPopup(true)}
                />
              </div>
            </section>

            {/* Horizontal Divider */}
            <div className="md:pt-[56px] md:pb-[20px]">
              <HorizontalDividerVector width="w-[174px] md:max-w-[1248px]" />
            </div>

            {/* Footer */}
            <section className="pb-[55px] pt-[20px] flex items-center justify-center md:p-0">
              <div className="flex flex-col justify-center items-center gap-[20px] w-fit md:w-full md:flex-row md:justify-between">
                <div className="text-[rgba(228,220,220,0.67)] text-center font-[Inter] text-[12px] not-italic font-normal leading-[18px]">
                  <span>{"Powered by "}</span>
                  <a
                    href="http://anchors.in"
                    target="_blank"
                    className="font-bold hover:underline"
                    onClick={() => mixpanel.track("Clicked Powered by Anchors")}
                  >
                    anchors.in
                  </a>
                </div>

                {/* Button */}
                <div className="md:hidden flex items-center gap-[12px]">
                  <span className="text-[#A3A3A3] text-center font-[Inter] text-[14px] not-italic font-normal leading-[18px]">
                    Influencer?
                  </span>
                  <button
                    className="flex px-[12px] py-[8px] justify-center items-center gap-[4.75px] self-stretch rounded-[6px] border-[1px] border-solid border-[#FFF] text-[#FFF] text-center font-[Inter] not-italic leading-[normal]"
                    onClick={() => {
                      mixpanel.track("Create your Collab Profile");
                      const url = `/influencer?refer=${
                        cookies?.referal_code || ""
                      }`;
                      window.open(url, "_blank");
                    }}
                  >
                    <span className="text-[16px] font-normal">🪄</span>
                    <span className="text-[12px] font-semibold">
                      Create your Collab Profile
                    </span>
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </>
  );
};
